import { useEffect, useMemo, useState } from 'react';
import { assetApi } from '../api/assetApi';

/** Module cache so positions + watchlist share lookups across mounts */
const nameCache = new Map(); // SYMBOL -> name | null (null = known miss)
const inflight = new Map(); // key -> Promise

function normalizeSymbols(symbols) {
  return [
    ...new Set(
      (symbols || [])
        .map((s) => String(s || '').trim().toUpperCase())
        .filter(Boolean)
    ),
  ];
}

async function fetchMissingNames(symbols) {
  const missing = symbols.filter((s) => !nameCache.has(s));
  if (!missing.length) return;

  const key = missing.slice().sort().join(',');
  if (inflight.has(key)) {
    await inflight.get(key);
    return;
  }

  const promise = (async () => {
    try {
      const res = await assetApi.getNames(missing);
      const data = res?.data || {};
      for (const sym of missing) {
        nameCache.set(sym, data[sym] || null);
      }
    } catch (err) {
      console.warn('Asset name lookup failed:', err?.message || err);
      for (const sym of missing) {
        if (!nameCache.has(sym)) nameCache.set(sym, null);
      }
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  await promise;
}

/**
 * Resolve company names for a list of tickers.
 * @param {string[]} symbols
 * @param {Record<string, string>} [seed] optional known names (e.g. from watchlist assets)
 * @returns {Record<string, string>}
 */
export function useAssetNames(symbols, seed = {}) {
  const normalized = useMemo(() => normalizeSymbols(symbols), [symbols]);
  const seedKey = useMemo(() => JSON.stringify(seed || {}), [seed]);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    // Seed cache from caller (watchlist asset.name, etc.)
    Object.entries(seed || {}).forEach(([sym, name]) => {
      const s = String(sym || '').toUpperCase();
      if (s && name && !nameCache.get(s)) nameCache.set(s, name);
    });

    let cancelled = false;
    (async () => {
      await fetchMissingNames(normalized);
      if (!cancelled) setVersion((v) => v + 1);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalized.join(','), seedKey]);

  return useMemo(() => {
    const out = {};
    for (const sym of normalized) {
      const name = nameCache.get(sym);
      if (name) out[sym] = name;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalized.join(','), version, seedKey]);
}

export function getCachedAssetName(symbol) {
  return nameCache.get(String(symbol || '').toUpperCase()) || '';
}
